import React, { useState, useCallback, useRef, useEffect } from 'react';
import FeatherIcon from 'feather-icons-react';
import { FilterConfig, User } from '../services/apiService';
import { MultiSelectDropdown } from './MultiSelectDropdown';

interface ReferralFilterPanelProps {
  onFiltersChange: (filters: FilterConfig) => void;
  isLoading: boolean;
  onSaveFilter?: () => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  availableOptions?: {
    referralTypes: string[];
    funders: string[];
    bodyParts: string[];
    providerOrgs: string[];
    imagingFacilities: string[];
    providerNames: string[];
    // AXISREF399: Clinic names from enum table, same pattern as other filter options
    clinicNames?: string[];
  };
  // New props for assignee filter (AXIS148)
  staffUsers?: User[];
  currentUserId?: string;
  userRole?: string;
  activeTab?: string;
  // AXIS164: Applied triage category from parent (e.g. restored from URL)
  appliedTriageCategory?: number[];
  // AXISREF399: Applied clinic names from parent (e.g. restored from URL)
  appliedClinicNames?: string[];
  // AXISREF350: My referrals vs all referrals toggle (reception queue roles)
  showQueueViewToggle?: boolean;
  queueView?: 'my' | 'all';
  onQueueViewChange?: (view: 'my' | 'all') => void;
  syncedSearch?: string;
}

interface FilterState {
  referralType: string[];
  funder: string[];
  bodyPart: string[];
  providerOrg: string[];
  imagingFacility: string[];
  providerName: string[];
  search: string;
  assignee: string; // AXIS148: Added assignee filter
  triageCategory: string[]; // AXIS164: Triage category multi-select as strings ("P1" etc.)
  clinic: string[]; // AXISREF399: Clinic names from enum, same pattern as other string filters
}

// AXIS164: Static triage category options (plain text, no colour coding)
const TRIAGE_CATEGORY_OPTIONS = ['P1', 'P2', 'P3', 'P4'];

export const ReferralFilterPanel: React.FC<ReferralFilterPanelProps> = ({
  onFiltersChange,
  isLoading,
  onSaveFilter,
  pageSize,
  onPageSizeChange,
  availableOptions,
  // AXIS148: New props for assignee filter
  staffUsers = [],
  currentUserId = '',
  userRole = '',
  activeTab = '',
  appliedTriageCategory,
  appliedClinicNames,
  showQueueViewToggle = false,
  queueView = 'my',
  onQueueViewChange,
  syncedSearch,
}) => {
  const [filters, setFilters] = useState<FilterState>({
    referralType: [],
    funder: [],
    bodyPart: [],
    providerOrg: [],
    imagingFacility: [],
    providerName: [],
    search: '',
    assignee: '', // AXIS148
    triageCategory: [], // AXIS164: Stored as strings e.g. "Category 1"
    clinic: [], // AXISREF399: Stored as clinic names
  });

  // AXIS148: Assignee dropdown state
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);

  // Track applied filters separately for display purposes
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(filters);

  // AXISREF399: Sync clinic filter from parent (e.g. URL restore on load) — same pattern as triage category
  useEffect(() => {
    if (appliedClinicNames && appliedClinicNames.length > 0) {
      setFilters((prev) => (prev.clinic.length === 0 ? { ...prev, clinic: appliedClinicNames } : prev));
      setAppliedFilters((prev) => (prev.clinic.length === 0 ? { ...prev, clinic: appliedClinicNames } : prev));
    }
  }, [appliedClinicNames?.join(',')]);

  // AXIS164: Sync triage category from parent when panel has none (e.g. URL restore on load)
  // Convert number[] from URL to string[] for internal state
  useEffect(() => {
    if (appliedTriageCategory && appliedTriageCategory.length > 0) {
      const asStrings = appliedTriageCategory.map((n) => `Category ${n}`);
      setFilters((prev) => (prev.triageCategory.length === 0 ? { ...prev, triageCategory: asStrings } : prev));
      setAppliedFilters((prev) => (prev.triageCategory.length === 0 ? { ...prev, triageCategory: asStrings } : prev));
    }
  }, [appliedTriageCategory?.join(',')]);

  // AXISREF350: Sync search from URL / parent handoff
  useEffect(() => {
    if (syncedSearch === undefined) {
      return;
    }
    setFilters((prev) => (prev.search === syncedSearch ? prev : { ...prev, search: syncedSearch }));
    setAppliedFilters((prev) => (prev.search === syncedSearch ? prev : { ...prev, search: syncedSearch }));
  }, [syncedSearch]);

  // AXIS148: Close assignee dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target as Node)) {
        setAssigneeDropdownOpen(false);
      }
    };

    if (assigneeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [assigneeDropdownOpen]);

  // AXIS148: Check if assignee filter should be shown (admin users on assigned tab)
  const showAssigneeFilter = userRole === 'admin' && activeTab === 'assigned';

  // Convert filters to FilterConfig — clinic names passed through directly, same as other string filters
  const buildFilterConfig = useCallback((filterState: FilterState): FilterConfig => {
    return {
      search: filterState.search || undefined,
      referralType: filterState.referralType.length > 0 ? filterState.referralType : undefined,
      funder: filterState.funder.length > 0 ? filterState.funder : undefined,
      bodyPart: filterState.bodyPart.length > 0 ? filterState.bodyPart : undefined,
      providerOrg: filterState.providerOrg.length > 0 ? filterState.providerOrg : undefined,
      imagingFacility: filterState.imagingFacility.length > 0 ? filterState.imagingFacility : undefined,
      providerName: filterState.providerName.length > 0 ? filterState.providerName : undefined,
      assignee: filterState.assignee || undefined, // AXIS148
      // AXIS164: Convert string labels (P1, P2, P3, P4) back to numbers for the API
      triageCategory:
        filterState.triageCategory.length > 0
          ? filterState.triageCategory.map((s) => parseInt(s.replace('P', ''), 10)).filter((n) => !isNaN(n))
          : undefined,
      // AXISREF399: Clinic names passed through as-is, same pattern as referralType, funder, etc.
      clinicNames: filterState.clinic.length > 0 ? filterState.clinic : undefined,
    };
  }, []);

  const handleFilterChange = (key: keyof FilterState, value: string | string[]) => {
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);
  };

  const handleMultiSelectChange = (key: keyof FilterState, selectedValues: string[]) => {
    const updatedFilters = { ...filters, [key]: selectedValues };
    setFilters(updatedFilters);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    const filterConfig = buildFilterConfig(filters);
    onFiltersChange(filterConfig);
  };

  const handleClearFilters = () => {
    const clearedFilters: FilterState = {
      referralType: [],
      funder: [],
      bodyPart: [],
      providerOrg: [],
      imagingFacility: [],
      providerName: [],
      search: '',
      assignee: '', // AXIS148
      triageCategory: [], // AXIS164: Stored as strings
      clinic: [], // AXISREF399
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setAssigneeSearchTerm(''); // AXIS148
    const filterConfig: FilterConfig = {};
    onFiltersChange(filterConfig);
  };

  const activeFilterCount = Object.entries(appliedFilters).filter(
    ([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value && typeof value === 'string' && value.trim();
    }
  ).length;

  const multiSelectField = (label: string, key: keyof FilterState, options: string[]) => {
    const selectedValues = filters[key] as string[];
    
    return (
      <MultiSelectDropdown
        label={label}
        options={options}
        selectedValues={selectedValues}
        onChange={(selected) => handleMultiSelectChange(key, selected)}
        disabled={isLoading}
      />
    );
  };

  const textField = (label: string, key: keyof FilterState, placeholder: string) => (
    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, fontSize: '0.9em' }}>
        {label}
      </label>
      <input
        type="text"
        placeholder={placeholder}
        value={filters[key] as string}
        onChange={(e) => handleFilterChange(key, e.target.value)}
        disabled={isLoading}
        className="form-control"
        style={{ fontSize: '0.9em', padding: '6px 8px', width: '100%' }}
      />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px', borderBottom: '1px solid #ddd' }}>
        <FeatherIcon icon="filter" size={20} />
        <h4 style={{ margin: 0, fontWeight: 600 }}>Filters</h4>
        {activeFilterCount > 0 && (
          <span
            style={{
              display: 'inline-block',
              backgroundColor: '#4a90e2',
              color: '#fff',
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '0.8em',
              fontWeight: 'bold',
              marginLeft: 'auto',
            }}
          >
            {activeFilterCount}
          </span>
        )}
      </div>

      {showQueueViewToggle && onQueueViewChange && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.9em' }}>
            Search scope
          </label>
          <div
            style={{
              display: 'flex',
              border: '1px solid #ccc',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            <button
              type="button"
              onClick={() => onQueueViewChange('my')}
              disabled={isLoading}
              style={{
                flex: 1,
                padding: '8px 10px',
                border: 'none',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontWeight: queueView === 'my' ? 600 : 400,
                background: queueView === 'my' ? 'var(--axis-vibrant-orange)' : '#fff',
                color: queueView === 'my' ? '#fff' : '#333',
              }}
            >
              My referrals
            </button>
            <button
              type="button"
              onClick={() => onQueueViewChange('all')}
              disabled={isLoading}
              style={{
                flex: 1,
                padding: '8px 10px',
                border: 'none',
                borderLeft: '1px solid #ccc',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontWeight: queueView === 'all' ? 600 : 400,
                background: queueView === 'all' ? 'var(--axis-vibrant-orange)' : '#fff',
                color: queueView === 'all' ? '#fff' : '#333',
              }}
            >
              All referrals
            </button>
          </div>
        </div>
      )}

      {/* All filter fields stacked vertically */}
      {textField('Search', 'search', 'Patient name, NHI, ACC...')}

      {/* AXIS148: Assignee filter dropdown for admin users on assigned tab */}
      {showAssigneeFilter && (
        <div ref={assigneeDropdownRef} style={{ position: 'relative', marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, fontSize: '0.9em' }}>
            Assignee {filters.assignee && '(1)'}
          </label>
          <div
            onClick={() => !isLoading && setAssigneeDropdownOpen(!assigneeDropdownOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              minHeight: '32px',
              padding: '6px 30px 6px 8px',
              borderRadius: '4px',
              backgroundColor: isLoading ? '#f5f5f5' : '#fff',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              position: 'relative',
              border: '1px solid #ccc',
            }}
          >
            {filters.assignee ? (
              <span style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                backgroundColor: '#e8f4f8',
                border: '1px solid #4a90e2',
                borderRadius: '12px',
                fontSize: '0.85em',
                color: '#333'
              }}>
                <span>
                  {filters.assignee === currentUserId 
                    ? 'Me' 
                    : staffUsers.find(u => u.id === filters.assignee)?.display_name || 'Unknown'}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilters({ ...filters, assignee: '' });
                    setAssigneeSearchTerm('');
                  }}
                  disabled={isLoading}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#4a90e2',
                    fontSize: '14px',
                    lineHeight: 1,
                  }}
                  title="Remove"
                >
                  <FeatherIcon icon="x" size={12} />
                </button>
              </span>
            ) : (
              <span style={{ color: '#999', fontSize: '0.9em', flex: 1 }}>
                Filter by assignee...
              </span>
            )}
            <span
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                color: '#999',
                pointerEvents: 'none'
              }}
            >
              <FeatherIcon icon={assigneeDropdownOpen ? "chevron-up" : "chevron-down"} size={16} />
            </span>
          </div>

          {assigneeDropdownOpen && !isLoading && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '2px',
              }}
            >
              {/* Search input */}
              <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                <input
                  type="text"
                  placeholder="Search staff..."
                  value={assigneeSearchTerm}
                  onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="form-control"
                  style={{ fontSize: '0.9em', padding: '6px 8px', width: '100%' }}
                />
              </div>

              {/* "Me" option at top */}
              <div
                onClick={() => {
                  setFilters({ ...filters, assignee: currentUserId });
                  setAssigneeDropdownOpen(false);
                  setAssigneeSearchTerm('');
                }}
                style={{
                  padding: '10px 12px',
                  cursor: 'pointer',
                  backgroundColor: filters.assignee === currentUserId ? '#e8f4f8' : '#f8f9fa',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 600,
                  fontSize: '0.9em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  if (filters.assignee !== currentUserId) {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = filters.assignee === currentUserId ? '#e8f4f8' : '#f8f9fa';
                }}
              >
                <FeatherIcon icon="user" size={16} />
                <span>Me</span>
                <span style={{ fontWeight: 400, color: '#666', marginLeft: 'auto' }}>
                  {staffUsers.find(u => u.id === currentUserId)?.display_name || ''}
                </span>
              </div>

              {/* Other staff members */}
              {staffUsers
                .filter(user =>
                  user.id !== currentUserId &&
                  (assigneeSearchTerm === '' ||
                    (user.display_name?.toLowerCase() ?? '').includes(assigneeSearchTerm.toLowerCase()) ||
                    (user.email?.toLowerCase() ?? '').includes(assigneeSearchTerm.toLowerCase()))
                )
                .map(user => (
                  <div
                    key={user.id}
                    onClick={() => {
                      setFilters({ ...filters, assignee: user.id });
                      setAssigneeDropdownOpen(false);
                      setAssigneeSearchTerm('');
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      backgroundColor: filters.assignee === user.id ? '#e8f4f8' : '#fff',
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: '0.9em',
                    }}
                    onMouseEnter={(e) => {
                      if (filters.assignee !== user.id) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = filters.assignee === user.id ? '#e8f4f8' : '#fff';
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{user.display_name}</div>
                    <div style={{ fontSize: '0.85em', color: '#666' }}>{user.email}</div>
                  </div>
                ))
              }

              {staffUsers.filter(user =>
                user.id !== currentUserId &&
                (assigneeSearchTerm === '' ||
                  (user.display_name?.toLowerCase() ?? '').includes(assigneeSearchTerm.toLowerCase()) ||
                  (user.email?.toLowerCase() ?? '').includes(assigneeSearchTerm.toLowerCase()))
              ).length === 0 && assigneeSearchTerm !== '' && (
                <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '0.9em' }}>
                  No staff members found
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* AXIS164: Triage Category multi-select dropdown (same styling as other filters, no colour coding) */}
      {multiSelectField('Priority', 'triageCategory', TRIAGE_CATEGORY_OPTIONS)}

      {/* AXISREF399: Clinic filter — options from filter_options enum, same pattern as other filters */}
      {multiSelectField('Clinic', 'clinic', availableOptions?.clinicNames || [])}

      {multiSelectField('Referral Type', 'referralType', availableOptions?.referralTypes || [])}
      {multiSelectField('Funder', 'funder', availableOptions?.funders || [])}
      {multiSelectField('Body Part', 'bodyPart', availableOptions?.bodyParts || [])}
      {multiSelectField('Referrer Org', 'providerOrg', availableOptions?.providerOrgs || [])}

      {/* Items Per Page */}
      <div style={{ paddingTop: '8px', borderTop: '1px solid #ddd', marginTop: '8px' }}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, fontSize: '0.9em' }}>
            Items Per Page
          </label>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={isLoading}
            className="form-control"
            style={{ fontSize: '0.9em', padding: '6px 8px', width: '100%' }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <button
          className="btn btn-secondary"
          onClick={handleClearFilters}
          disabled={isLoading || activeFilterCount === 0}
          style={{ fontSize: '0.85em', padding: '6px 10px', flex: 1 }}
        >
          Clear
        </button>
        <button
          className="btn btn-primary"
          onClick={handleApplyFilters}
          disabled={isLoading}
          style={{ fontSize: '0.85em', padding: '6px 10px', flex: 1 }}
        >
          Apply Filters
        </button>
        {onSaveFilter && (
          <button
            className="btn btn-success"
            onClick={onSaveFilter}
            disabled={isLoading || activeFilterCount === 0}
            style={{ fontSize: '0.85em', padding: '6px 10px', flex: 1 }}
          >
            💾 Save
          </button>
        )}
      </div>
    </div>
  );
};
